import { Controller } from "@hotwired/stimulus";
import Dropzone from "dropzone";
import { Turbo } from "@hotwired/turbo-rails";
import { flashDangerAlert } from "../includes/flashAlert";

export default class extends Controller {
  static targets = ["categorySelect", "categoryInput"];

  connect () {
    this.element.querySelectorAll(".dz-preview").forEach(preview => preview.remove());
    this.element.classList.remove("dz-started");
    this.preparingFiles = new Set();
    this.myDropzone = new Dropzone(this.element, {
      headers: { Accept: "text/vnd.turbo-stream.html, text/html, application/xhtml+xml" },
      accept: (file, done) => this.prepareFile(file, done),
      transformFile: (file, done) => done(file.unlockedCopy || file),
    });
    this.uploadErrors = [];
    this.hadUploadSuccess = false;

    this.myDropzone.on("success", (file, response) => {
      delete file.unlockedCopy;
      // Two different callers wrap this controller around their <form>: one whose create action
      // renders a turbo_stream (updating the current page in place), and one whose create action
      // answers with a redirect target as JSON. Turbo.renderStreamMessage doesn't simply ignore
      // the latter (it parses whatever it's given as a fragment and unconditionally appends it
      // onto document.documentElement) -- fed an unrelated JSON body, it would corrupt the current
      // DOM rather than navigating anywhere. Branching on the actual response's content type is
      // what makes this controller safe to reuse for either caller.
      if (file.xhr?.getResponseHeader("Content-Type")?.includes("turbo-stream")) {
        Turbo.renderStreamMessage(response);
        this.hadUploadSuccess = true;
      } else if (typeof response === "object" && response !== null && response.redirect_url) {
        // Deferred to queuecomplete, not visited here directly -- dropping several files fires
        // one success/error per file, and navigating away on the first would abandon whichever
        // others are still queued or uploading. Every file's outcome is collected here and
        // attached to the one navigation queuecomplete performs, as a query param rather than a
        // session flash: each file's create request is its own hop, and Rails only carries a
        // flash across exactly one of those.
        this.redirectUrl = response.redirect_url;
        this.hadUploadSuccess = true;
      } else {
        // Dropzone calls this "success" purely off the XHR's 2xx status -- a session expiring
        // mid-upload, say, XHR-follows the redirect to a sign-in page that itself answers 200,
        // with no create ever happening. Neither branch above fits that response, so it lands
        // here rather than being misread as a create this controller's callers never actually
        // produce a bare 2xx-with-no-redirect_url for.
        const fallback = "Upload failed — the server returned an unexpected response. Please try again.";
        this.uploadErrors.push(fallback);
        file.previewElement?.classList.remove("dz-success");
        file.previewElement?.classList.add("dz-error");
        file.previewElement?.querySelectorAll("[data-dz-errormessage]").forEach((node) => {
          node.textContent = fallback;
        });
      }
    });

    this.myDropzone.on("error", (file, response) => {
      delete file.unlockedCopy;
      if (file.cancelled) {
        this.myDropzone.removeFile(file);
        return;
      }
      // Dropzone only hands over a parsed object here when the server answered JSON. A rejection
      // before any request was sent (no file.xhr at all -- e.g. a client-side maxFilesize check)
      // or a non-JSON server failure (a 500 HTML page, a dropped connection) hands over a plain
      // string instead. Reading redirect_url/error off that string would clobber a redirectUrl a
      // success already set, and inferring "no error key" as a success would misreport a file
      // that never uploaded as one that did.
      if (typeof response === "object" && response !== null) {
        this.redirectUrl ??= response.redirect_url;
        this.uploadErrors.push(response.error || "Upload failed.");
      } else if (file.xhr) {
        // A non-JSON server body (Dropzone passes xhr.responseText straight through) is often an
        // entire error page, not a sentence -- not something to show a human or put on a URL query
        // param. A client-side rejection (no request ever sent, so no file.xhr) is Dropzone's own
        // dictionary string instead, which is worth keeping as-is.
        const fallback = "Upload failed — the server returned an error. Please try again.";
        this.uploadErrors.push(fallback);
        // Dropzone's own default error listener already wrote the raw response into this file's
        // preview tooltip, since that listener runs before this one and reads `response` itself --
        // this is the only visible surface at all when this is the one file in the queue, since a
        // lone failure here also never navigates anywhere to show the aggregated message. Runs
        // after Dropzone's own write so the fallback is what's left on screen.
        file.previewElement?.querySelectorAll("[data-dz-errormessage]").forEach((node) => {
          node.textContent = fallback;
        });
      } else {
        this.uploadErrors.push(response);
      }
    });

    this.myDropzone.on("queuecomplete", () => {
      // Dropzone can emit this deferred event before every file dropped alongside this one has
      // actually been added: a client-side rejection (accept()'s maxFilesize/acceptedFiles checks)
      // settles synchronously inside addFile, before a sibling from the same drop reaches the queue.
      // Re-checking counts here, when this handler actually runs, catches that -- by then a real
      // sibling is genuinely queued or uploading.
      if (!this.element.isConnected || this.preparingFiles.size > 0 || this.myDropzone.getQueuedFiles().length > 0 || this.myDropzone.getUploadingFiles().length > 0) return;

      // A non-JSON failure (above) is the one outcome that can leave redirectUrl unset -- every
      // other outcome navigates away, tearing this controller down. Nothing will navigate here, so
      // Dropzone's own hover-only tooltip would otherwise be the only trace of the failure.
      if (!this.redirectUrl) {
        if (this.uploadErrors.length > 0) flashDangerAlert("File upload error", this.uploadErrors.join("; "));
        const completedSuccessfully = this.hadUploadSuccess && this.uploadErrors.length === 0;
        this.uploadErrors = [];
        this.hadUploadSuccess = false;
        if (completedSuccessfully) {
          this.myDropzone.removeAllFiles();
          this.dispatch("uploads-complete");
        }
        return;
      }

      const url = new URL(this.redirectUrl, window.location.origin);
      if (this.hadUploadSuccess) url.searchParams.set("upload_success", "1");
      if (this.uploadErrors.length > 0) url.searchParams.set("upload_alert", this.uploadErrors.join("; "));
      Turbo.visit(url.toString());
    });

    // Dropzone uploads on drop, before any server validation runs -- a form with a required
    // category select must not accept a drop until one is chosen, or the file uploads and only
    // then gets rejected. sl-select isn't a native <select>, so its value has to be relayed to a
    // real hidden input by hand (categoryChanged, below) for the server to receive it at all.
    if (this.hasCategorySelectTarget) {
      this.myDropzone.disable();
    }

    this.myDropzone.on("sending", (file, _xhr, formData) => {
      if (file.uploadCategory) formData.set(this.categoryInputTarget.name, file.uploadCategory);
    });
  }

  disconnect () {
    this.myDropzone.destroy();
    this.preparingFiles.clear();
  }

  prepareFile (file, done) {
    if (this.hasCategoryInputTarget) file.uploadCategory = this.categoryInputTarget.value;
    this.preparingFiles.add(file);
    const complete = (output, error) => {
      if (output) file.unlockedCopy = output;
      this.preparingFiles.delete(file);
      done(error);
    };
    const event = this.dispatch("prepare-file", { detail: { file, complete }, cancelable: true });
    if (!event.defaultPrevented) complete();
  }

  categoryChanged (event) {
    this.categoryInputTarget.value = event.target.value;
    if (event.target.value) {
      this.myDropzone.enable();
    } else {
      this.myDropzone.disable();
    }
  }

  resetCategory () {
    if (!this.hasCategorySelectTarget) return;

    this.categorySelectTarget.value = "";
    this.categoryInputTarget.value = "";
    this.myDropzone.disable();
  }
}
